import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface PlanFeatureMatrix {
  dailyProfileLimit: number;
  unlimitedSearch: boolean;
  contactView: boolean;
  unlimitedInterests: boolean;
  monthlyInterestLimit: number;
  profileBoost: boolean;
  featuredProfile: boolean;
  horoscopeDownload: boolean;
  aiSuggestions: boolean;
  chatAccess: boolean;
  viewAnnualEarnings?: boolean;
  savedFilters: number;
  favoritesLimit?: number;
}

export interface PlanItem {
  plan_code: string;
  display_name: string;
  tagline: string;
  display_order: number;
  pricing: Array<{ cycle: string; price: number; duration_days: number }>;
  ui_feature_list: string[];
}

export interface CurrentSubscription {
  user_id: string;
  keycloak_id?: string;
  current_plan: string;
  subscription_status: string;
  plan_expiry?: string;
  boost_until?: string;
  usage: {
    interests_sent_this_month: number;
    favorites_this_month: number;
    profile_views_today: number;
  };
  features: PlanFeatureMatrix;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private apiUrl = `${environment.apiUrl}/subscription`;
  private currentSubSubject = new BehaviorSubject<CurrentSubscription | null>(null);
  public currentSubscription$ = this.currentSubSubject.asObservable();

  constructor(private http: HttpClient) {
    this.initFromStorage();
  }

  boostProfile(profileId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/boost-profile`, { profileId });
  }

  getBoostStatus(profileId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/boost-status?profileId=${profileId}`);
  }

  private initFromStorage(): void {
    const cached = localStorage.getItem('current_subscription');
    if (cached) {
      try {
        this.currentSubSubject.next(JSON.parse(cached));
      } catch (e) {
        localStorage.removeItem('current_subscription');
      }
    }
  }

  fetchCurrentSubscription(profileIdOrKeycloak: string): Observable<any> {
    if (!profileIdOrKeycloak) return of(null);
    return this.http.get<any>(`${this.apiUrl}/current?profileId=${profileIdOrKeycloak}`).pipe(
      tap(res => {
        if (res && res.status === 'success' && res.data) {
          this.setCurrentSubscription(res.data);
        }
      }),
      catchError(err => {
        console.warn('[SubscriptionService] Error fetching current subscription:', err);
        return of(null);
      })
    );
  }

  setCurrentSubscription(sub: CurrentSubscription): void {
    localStorage.setItem('current_subscription', JSON.stringify(sub));
    localStorage.setItem('user_current_plan', sub.current_plan);
    this.currentSubSubject.next(sub);
  }

  getCurrentPlanName(): string {
    const sub = this.currentSubSubject.value;
    return sub ? sub.current_plan : (localStorage.getItem('user_current_plan') || 'FREE');
  }

  hasFeature(featureName: keyof PlanFeatureMatrix): boolean {
    const sub = this.currentSubSubject.value;
    if (!sub || !sub.features) return false;
    return Boolean(sub.features[featureName]);
  }

  getPlans(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/plans`);
  }

  refreshCache(): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/refresh-cache`, {});
  }

  getFeatures(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/features`);
  }

  createOrder(plan: string, cycle: string, profileId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/create-order`, { plan, cycle, profileId });
  }

  verifySignature(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/verify-signature`, payload);
  }

  loadRazorpaySDK(): Promise<boolean> {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        return resolve(true);
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }
}
